import {
  type GroupType,
  getCodes,
  orEmptyArray,
} from '@lms/api-client/backend';
import {
  CategoryBarChart,
  ChartCard,
  DonutChart,
  foldSeries,
  formatNumber,
  intlLocale,
  KpiTile,
  ShareRows,
  useSeries,
} from '@lms/charts';
import { cn } from '@lms/ui/utils';
import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { useLocale, useTranslations } from 'use-intl';
import { MultiSelect } from '@/components/multi-select';
import { SearchFilter } from '@/components/search-filter';
import { SimpleSelect } from '@/components/simple-select';
import type { Site } from '@/features/branding/brand-provider';
import { CompletedTrainingsCard } from '@/features/dashboard/components/completed-trainings-card';
import { DownloadStatsLink } from '@/features/dashboard/components/download-stats-link';
import { EducationTotal } from '@/features/dashboard/components/education-total';
import { GroupCompletions } from '@/features/dashboard/components/group-completions';
import { PeriodSelector } from '@/features/dashboard/components/period-selector';
import {
  StatsGrid,
  StatsPlaceholder,
  StatsToolbar,
  usePeriodLabel,
  WIDE,
} from '@/features/dashboard/components/stats-layout';
import {
  countWithCompletions,
  curriculumShares,
  expectedFromCodes,
  groupCompletions,
  groupsOfType,
  useCoverage,
} from '@/features/dashboard/coverage';
import {
  averageCurriculumMinutes,
  curriculumTotalSeconds,
  educationMinutes,
} from '@/features/dashboard/education-time';
import { type StatsHookParams, useStats } from '@/features/dashboard/use-stats';

const curriculumOf = (r: { curriculumName: string }) => r.curriculumName;
const countOf = (r: { count: number }) => r.count;

/**
 * Dashboard for a single location: how many trainings are completed and
 * when, and which groups have completions — overall or per curriculum. Every
 * figure follows the selected period and filters.
 */
export function LocationStats({ site }: { site: Site }) {
  const t = useTranslations('admin');
  const t_stats = useTranslations('stats');
  const locale = useLocale();

  const [filter, setFilter] = useState<StatsHookParams>({
    locations: [site.id],
    courses: [],
    period: 'year',
    fromDate: new Date(),
    compare: true,
    groups: [],
    codes: [],
  });
  // One curriculum for the whole page (null: all), set from the toolbar, the
  // charts or the per-curriculum rows.
  const [curriculumId, setCurriculumId] = useState<number | null>(null);
  const toggleCurriculum = (id: number) =>
    setCurriculumId((current) => (current === id ? null : id));

  const stats = useStats({
    ...filter,
    curricula: curriculumId === null ? [] : [curriculumId],
  });
  const selectCurriculumByName = (name: string) => {
    const match = stats.curricula.find((c) => c.name === name);
    if (match) toggleCurriculum(match.id);
  };
  const coverage = useCoverage(
    site.hasGroups ? site.id : undefined,
    locale,
    { period: filter.period, fromDate: filter.fromDate },
    filter.codes,
  );
  const { data: codes, isLoading: codesLoading } = useSWR(
    ['admin/codes', site.id],
    () => orEmptyArray(getCodes(site.id)),
  );
  const periodLabel = usePeriodLabel(filter.period, filter.fromDate);

  // One colour per curriculum, shared by every chart on the page. Curricula
  // that only appear in the education-time data still get a colour.
  const curriculumRows = useMemo(
    () => [
      ...stats.sessions,
      ...stats.educationTime.map((e) => ({ ...e, count: 0 })),
    ],
    [stats.sessions, stats.educationTime],
  );
  const curricula = useSeries(curriculumRows, curriculumOf, countOf, {
    otherLabel: t_stats('other'),
  });
  const donutCurricula = useMemo(
    () => foldSeries(curricula, 6, t_stats('other')),
    [curricula, t_stats],
  );
  const minutes = useMemo(
    () => educationMinutes(stats.educationTime),
    [stats.educationTime],
  );
  const averageMinutes = useMemo(
    () => averageCurriculumMinutes(minutes),
    [minutes],
  );
  const timeTotals = useMemo(
    () => curriculumTotalSeconds(stats.educationTime),
    [stats.educationTime],
  );

  const selectedGroups = filter.groups ?? [];
  const groupSection = useMemo(() => {
    if (!coverage.data) return undefined;
    // With a group filter, every group figure is limited to those groups.
    const selected = new Set(selectedGroups);
    const data =
      selected.size === 0
        ? coverage.data
        : {
            ...coverage.data,
            groups: coverage.data.groups.filter((g) => selected.has(g.id)),
            cells: coverage.data.cells.filter((c) => selected.has(c.groupId)),
          };
    // Which groups are expected to take which curricula, from the codes
    // (only the selected ones when filtering by code).
    const selectedCodes = new Set(filter.codes ?? []);
    const expected = expectedFromCodes(
      (codes ?? []).filter(
        (c) => selectedCodes.size === 0 || selectedCodes.has(c.id),
      ),
    );
    const shares = {
      tenant: curriculumShares(data, 'tenant', expected),
      supplier: curriculumShares(data, 'supplier', expected),
    };
    // With a curriculum selected, the KPIs count the groups in scope for it
    // (expected via codes, or with completions in it anyway).
    const counts = (type: GroupType) => {
      const selected = shares[type].find((r) => r.id === curriculumId);
      return selected
        ? { of: selected.total, with: selected.value }
        : {
            of: groupsOfType(data, type).length,
            with: countWithCompletions(data, type),
          };
    };
    const tenants = counts('tenant');
    const suppliers = counts('supplier');
    return {
      tenants: tenants.of,
      suppliers: suppliers.of,
      tenantsWith: tenants.with,
      suppliersWith: suppliers.with,
      // Per type, only the curricula with groups of that type in scope.
      tenantShares: shares.tenant.filter((r) => r.total > 0),
      supplierShares: shares.supplier.filter((r) => r.total > 0),
      list: groupCompletions(data, curriculumId, expected, intlLocale(locale)),
    };
  }, [
    coverage.data,
    codes,
    filter.codes,
    selectedGroups,
    curriculumId,
    locale,
  ]);

  // "Butiker & Leverantörer", as in the admin sidebar; just the alias when
  // the location has no suppliers.
  const hasAnySuppliers =
    coverage.data?.groups.some((g) => g.groupType === 'supplier') ?? false;
  const groupsHeading = hasAnySuppliers
    ? `${t(site.groupAlias)} & ${t('suppliers')}`
    : t(site.groupAlias);

  const toolbar = (
    <StatsToolbar>
      {stats.courses.length > 0 && (
        <div className="w-full lg:max-w-72">
          <MultiSelect
            onSelect={(courses) => setFilter((f) => ({ ...f, courses }))}
            options={stats.courses.map((a) => ({ value: a.id, label: a.name }))}
            prefix={t('courses')}
          />
        </div>
      )}
      {coverage.data && coverage.data.groups.length > 0 && (
        <div className="w-full lg:max-w-72">
          <SearchFilter
            prefix={groupsHeading}
            sections={[
              { label: t(site.groupAlias), type: 'tenant' },
              { label: t('suppliers'), type: 'supplier' },
            ]
              .map(({ label, type }) => ({
                label,
                items: (coverage.data?.groups ?? [])
                  .filter((g) => g.groupType === type)
                  .map((g) => ({ id: g.id, label: g.name })),
              }))
              .filter((s) => s.items.length > 0)}
            selected={selectedGroups}
            onChange={(groups) => setFilter((f) => ({ ...f, groups }))}
            searchPlaceholder={t('search')}
            emptyText={t('no_matches')}
          />
        </div>
      )}
      {codes && codes.length > 0 && (
        <div className="w-full lg:max-w-72">
          <SearchFilter
            prefix={t('codes')}
            sections={[
              {
                items: codes.map((c) => ({
                  id: c.id,
                  label: c.name ? `${c.code} – ${c.name}` : c.code,
                })),
              },
            ]}
            selected={filter.codes ?? []}
            onChange={(codes) => setFilter((f) => ({ ...f, codes }))}
            searchPlaceholder={t_stats('search_codes')}
            emptyText={t_stats('no_matching_codes')}
          />
        </div>
      )}
      {stats.curricula.length > 0 && (
        <div className="w-full lg:max-w-72">
          <SimpleSelect
            value={curriculumId === null ? 'all' : String(curriculumId)}
            onValueChange={(v) =>
              setCurriculumId(v === 'all' ? null : Number(v))
            }
            prefix={t_stats('curriculum')}
            className="w-full"
            options={[
              { value: 'all', label: t_stats('any_curriculum') },
              ...stats.curricula.map((c) => ({
                value: String(c.id),
                label: c.name,
              })),
            ]}
          />
        </div>
      )}
      <div className="w-full lg:max-w-72">
        <PeriodSelector
          startDate={new Date('2024-01-01T00:00:00+00:00')}
          onChange={(period, fromDate) =>
            setFilter((f) => ({ ...f, fromDate, period }))
          }
        />
      </div>
    </StatsToolbar>
  );

  const error = stats.error ?? coverage.error;
  if (stats.isLoading || coverage.isLoading || codesLoading || error) {
    return (
      <>
        {toolbar}
        <StatsPlaceholder state={error ? 'error' : 'loading'} error={error} />
      </>
    );
  }

  const compared = filter.period !== 'since';
  const tenantLabel = t(site.groupAlias);
  const supplierLabel = t('suppliers');
  const hasTenants = (groupSection?.tenants ?? 0) > 0;
  const hasSuppliers = (groupSection?.suppliers ?? 0) > 0;
  // One share card per group type the location has (whatever the filter).
  const shareCards = groupSection
    ? [
        {
          type: 'tenant' as const,
          label: tenantLabel,
          rows: groupSection.tenantShares,
        },
        {
          type: 'supplier' as const,
          label: supplierLabel,
          rows: groupSection.supplierShares,
        },
      ].filter(
        ({ type }) =>
          coverage.data?.groups.some((g) => g.groupType === type) ?? false,
      )
    : [];

  return (
    <>
      {toolbar}
      <StatsGrid>
        <ChartCard
          title={t_stats('completed_trainings')}
          description={periodLabel}
        >
          <DonutChart
            data={stats.sessions}
            name={curriculumOf}
            value={countOf}
            seriesSet={donutCurricula}
            centerLabel={t_stats('trainings_lower')}
            locale={locale}
            emptyLabel={t_stats('no_data')}
            onSelect={selectCurriculumByName}
          />
        </ChartCard>

        {groupSection && (hasTenants || hasSuppliers) && (
          <div className="grid auto-rows-fr gap-4">
            {hasTenants && (
              <KpiTile
                label={tenantLabel}
                description={t_stats('with_completions')}
                value={groupSection.tenantsWith}
                of={groupSection.tenants}
                locale={locale}
              />
            )}
            {hasSuppliers && (
              <KpiTile
                label={supplierLabel}
                description={t_stats('with_completions')}
                value={groupSection.suppliersWith}
                of={groupSection.suppliers}
                locale={locale}
              />
            )}
          </div>
        )}

        <ChartCard
          title={t_stats('course_time')}
          description={periodLabel}
          value={
            <EducationTotal
              totals={timeTotals}
              seriesSet={curricula}
              locale={locale}
            />
          }
          className="md:col-span-2 lg:col-span-1"
        >
          <CategoryBarChart
            data={minutes}
            category={curriculumOf}
            stack={(m) => m.courseName}
            value={(m) => m.minutes}
            seriesSet={curricula}
            colorBy="category"
            valueFormatter={(v) =>
              t_stats('minutes_value', { value: formatNumber(v, locale) })
            }
            reference={
              averageMinutes > 0 && minutes.length > 1
                ? {
                    value: averageMinutes,
                    label: `${t_stats('average')} ${averageMinutes} min`,
                  }
                : undefined
            }
            locale={locale}
            labelWidth={104}
            emptyLabel={t_stats('no_data')}
            totalLabel={t_stats('total')}
            onSelect={selectCurriculumByName}
          />
        </ChartCard>

        <CompletedTrainingsCard
          sessions={stats.sessions}
          previous={
            compared
              ? {
                  data: stats.previousSessions,
                  label: t_stats('same_period_last_year'),
                }
              : undefined
          }
          seriesSet={curricula}
          filter={filter}
          periodLabel={periodLabel}
        />

        {groupSection && (
          <>
            <h2 className={`${WIDE} mt-4 text-lg font-semibold tracking-tight`}>
              {groupsHeading}
            </h2>

            <div
              className={cn(
                WIDE,
                'grid gap-4',
                shareCards.length > 1 && 'md:grid-cols-2',
              )}
            >
              {shareCards.map(({ type, label, rows }) => (
                <ChartCard
                  key={type}
                  title={label}
                  description={t_stats('per_curriculum_title')}
                >
                  <ShareRows
                    rows={rows}
                    describe={(r) =>
                      `${formatNumber(r.value, locale)} / ${formatNumber(r.total, locale)}`
                    }
                    selected={curriculumId ?? undefined}
                    onSelect={(id) => toggleCurriculum(Number(id))}
                    locale={locale}
                  />
                </ChartCard>
              ))}
            </div>

            <ChartCard
              title={t_stats('completed_trainings')}
              value={`${formatNumber(
                groupSection.list.rows.filter((r) => !r.missing).length,
                locale,
              )} / ${formatNumber(groupSection.list.rows.length, locale)}`}
              className={WIDE}
            >
              <GroupCompletions
                rows={groupSection.list.rows}
                curricula={groupSection.list.curricula}
                groupHref={(id) => `/${site.slug}/admin/groups/${id}`}
                types={
                  hasTenants && hasSuppliers
                    ? {
                        heading: t_stats('type'),
                        labels: {
                          tenant: t(`${site.groupAlias}_singular`),
                          supplier: t('suppliers_singular'),
                        },
                      }
                    : undefined
                }
              />
            </ChartCard>
          </>
        )}
      </StatsGrid>
      <DownloadStatsLink site={site} />
    </>
  );
}
